import { resolveHex } from './colorTokens';
import {
  CandlestickColors,
  ChartTypeKey,
  ComparisonItem,
  DrawingToolConfig,
  DrawingToolKey,
  IntervalKey,
  LineConfig,
  PeriodKey,
  TAItem,
  ViewState,
} from './types';
import {
  buildChartPayload,
  ChartBundlePayload,
  csvEdgeDates,
  DEFAULT_CHART_OPTION,
  getShowFromByPeriod,
} from './chartProtocol';

export const LINE_STYLE_MAP: Record<string, string> = {
  s: 's',
  d: 'd',
};

const hex = (c: string) => resolveHex(c);

const DRAW_TOOL_KEY_MAP: Record<DrawingToolKey, string> = {
  line: 'drawLine',
  retracement: 'drawRetracement',
  fans: 'drawFan',
  arcs: 'drawArcs',
};

export function getDrawTool(
  activeTool: DrawingToolKey | null,
  moveEnabled: boolean,
): string {
  if (moveEnabled || !activeTool) return 'move';
  return DRAW_TOOL_KEY_MAP[activeTool];
}

export function buildDrawToolOption(
  configs: Record<DrawingToolKey, DrawingToolConfig>,
): Record<string, unknown> {
  const { line, retracement, fans, arcs } = configs;
  const s = (style: string) => LINE_STYLE_MAP[style] ?? 's';
  return {
    drawLineColor: hex(line.color),
    drawLineThickness: line.thickness,
    drawLineStyle: s(line.style),
    drawRetracementColor: hex(retracement.color),
    drawRetracementThickness: retracement.thickness,
    drawRetracementStyle: s(retracement.style),
    drawFanColor: hex(fans.color),
    drawFanThickness: fans.thickness,
    drawFanStyle: s(fans.style),
    drawArcsColor: hex(arcs.color),
    drawArcsThickness: arcs.thickness,
    drawArcsStyle: s(arcs.style),
    drawLabelText: 'A',
    drawLabelBody: 'Anno',
    drawLabelTextColor: 'FFFFFF',
    drawLabelColor: hex(line.color),
    drawLabelFocusColor: hex(line.color),
    drawLabelSelectColor: '333333',
  };
}

export function buildChartOption(
  chartType: ChartTypeKey,
  candlestickColors: CandlestickColors,
  lineConfig: LineConfig,
  barColor: string,
  comparisonMode = false,
): Record<string, unknown> {
  const base = { ...DEFAULT_CHART_OPTION };
  // In compare mode the bundle forces line rendering for the main stock,
  // so always include line fields so thickness/style/color take effect.
  const lineFields = comparisonMode
    ? {
        lineColor: hex(lineConfig.color),
        lineThickness: lineConfig.thickness,
        lineStyle: LINE_STYLE_MAP[lineConfig.style] ?? 's',
      }
    : {};
  if (chartType === 'candlestick') {
    return {
      ...base,
      candleWidth: 6,
      upColor: hex(candlestickColors.upLine),
      upFillColor: hex(candlestickColors.upBody),
      downColor: hex(candlestickColors.downLine),
      downFillColor: hex(candlestickColors.downBody),
      ...lineFields,
    };
  }
  if (chartType === 'line') {
    return {
      ...base,
      lineColor: hex(lineConfig.color),
      lineThickness: lineConfig.thickness,
      lineStyle: LINE_STYLE_MAP[lineConfig.style] ?? 's',
    };
  }
  if (chartType === 'bar') {
    return { ...base, barColor: hex(barColor), ...lineFields };
  }
  return { ...base, ...lineFields };
}

// Overlays drawn on main chart; everything else goes to sub-chart indicator panel
export const OVERLAY_IDS = new Set([
  'SMA',
  'WMA',
  'EMA',
  'MEMA',
  'TVMA',
  'ICH',
  'LRT',
  'SAR',
  'BB',
  'VAP',
  'ENV',
  'PRR',
  'VWAP',
  'WC',
  'TP',
  'KC',
  'FIB',
]);

// Our internal config key → bundle key where they differ
export const BUNDLE_KEY_MAP: Record<string, string> = { BIAS: 'Bias', RHI: 'SHI' };

const RC_MKT_MAP: Record<string, string> = {
  's&p500': '.SPX',
  'asx200': '.AXJO',
  'nasdaq': '.IXIC',
};

export function serializeTA(ta: TAItem): unknown {
  const lines = ta.lines ?? [];
  const lineColor = (i: number) => hex(lines[i]?.color ?? '');
  const lineEnabled = (i: number, def = true) => lines[i]?.enabled ?? def;
  const lineValue = (i: number, def: number) =>
    (lines[i]?.period as number) ?? def;
  const mainColor = hex(ta.color);
  const mainPeriod = ta.period;
  const selectVal = (i: number, def: string) =>
    (ta.selectValues?.[i] ?? def).toLowerCase();

  switch (ta.key) {
    // TREND
    case 'SMA':
    case 'WMA':
    case 'EMA':
    case 'MEMA':
      // Array of 5 slots; disabled lines sent as empty {}
      return lines.map((line) =>
        line.enabled
          ? {
              period: line.period,
              color: hex(line.color),
              thickness: 2,
              style: 's',
              enabled: true,
              showTracker: true,
            }
          : {},
      );

    case 'TVMA':
      return {
        period: lineValue(0, 10),
        color: mainColor,
        thickness: 2,
        style: 's',
        enabled: true,
        showTracker: true,
        ta: 'TVMA',
        value: lineValue(0, 10),
        name: 'Turnover Volume Moving Average (TVMA)',
      };

    case 'ICH':
      return {
        CONV_LINE: {
          style: 's',
          thickness: 2,
          color: lineColor(0),
          showTracker: true,
          period: lineValue(0, 9),
          enabled: lineEnabled(0),
        },
        BASE_LINE: {
          style: 's',
          thickness: 2,
          color: lineColor(1),
          showTracker: true,
          period: lineValue(1, 26),
          enabled: lineEnabled(1),
        },
        LEADING_SPAN_A: {
          style: 's',
          thickness: 2,
          color: lineColor(2),
          showTracker: true,
          enabled: lineEnabled(2),
        },
        LEADING_SPAN_B: {
          style: 's',
          thickness: 2,
          color: lineColor(3),
          showTracker: true,
          period: lineValue(3, 52),
          enabled: lineEnabled(3),
        },
        LAGGING_SPAN: {
          style: 's',
          thickness: 2,
          color: lineColor(4),
          showTracker: true,
          enabled: lineEnabled(4),
        },
        TREND_CLOUD: { upColor: lineColor(5), downColor: lineColor(6) },
      };

    case 'MACD':
      return {
        p1: lineValue(0, 12),
        p2: lineValue(1, 26),
        p3: lineValue(2, 9),
        color: lineColor(0),
        thickness: 2,
        style: 's',
        color3: lineColor(2),
        thickness3: 2,
        style3: 's',
        upColor: lineColor(3),
        downColor: lineColor(4),
      };

    case 'LRT':
      return { style: 's', thickness: 2, color: mainColor, showTracker: true };

    case 'SAR':
      return {
        color: mainColor,
        default: lineValue(0, 0.02),
        increment: lineValue(1, 0.02),
        maximum: lineValue(2, 0.2),
        showTracker: true,
      };

    case 'ADXR':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        DMI_period: lineValue(0, 14),
        ADX_period: lineValue(1, 6),
      };

    case 'DMI':
      return {
        period: lineValue(0, 14),
        DI: {
          style: 's',
          thickness: 2,
          color: mainColor,
          showTracker: true,
          posColor: lineColor(0),
          negColor: lineColor(1),
          enabled: lineEnabled(0),
        },
        ADX: {
          style: 's',
          thickness: 2,
          color: lineColor(2),
          showTracker: true,
          enabled: lineEnabled(2),
        },
      };

    case 'BIAS':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 14),
      };

    case 'ALF':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 14),
      };

    // VOLATILITY
    case 'BB':
      // lines: [0]=Period, [1]=SD1σ+, [2]=SD1σ-, [3]=SD2σ+, [4]=SD2σ-, [5]=SD3σ+, [6]=SD3σ-
      return {
        BB: {
          period: lineValue(0, 25),
          color: lineColor(0),
          thickness: 2,
          style: 'd',
          showTracker: true,
          enabled: lineEnabled(0),
        },
        deviation: [
          {
            value: Number(selectVal(1, '1')),
            color: lineColor(1),
            color2: lineColor(2),
            thickness: 2,
            style: 's',
            showTracker: true,
            enabled: lineEnabled(1),
          },
          {
            value: Number(selectVal(3, '2')),
            color: lineColor(3),
            color2: lineColor(4),
            thickness: 2,
            style: 's',
            showTracker: true,
            enabled: lineEnabled(3),
          },
          {
            value: Number(selectVal(5, '3')),
            color: lineColor(5),
            color2: lineColor(6),
            thickness: 2,
            style: 's',
            showTracker: true,
            enabled: lineEnabled(5),
          },
        ],
      };

    case 'VC':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 14),
      };
    case 'ATR':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 14),
      };
    case 'SD':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 20),
      };
    case 'VltyCC':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 10),
      };

    // OSCILLATOR
    case 'KRI':
      return {
        method: selectVal(2, 'sma'),
        module: selectVal(3, 'close'),
        options: [
          {
            style: 's',
            thickness: 2,
            color: lineColor(0),
            showTracker: true,
            period: lineValue(0, 14),
            enabled: lineEnabled(0),
          },
          {
            style: 's',
            thickness: 2,
            color: lineColor(1),
            showTracker: true,
            period: lineValue(1, 3),
            enabled: lineEnabled(1),
          },
        ],
      };

    case 'PL':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 20),
      };
    case 'RCI':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 14),
      };
    case 'CCI':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 14),
      };
    case 'DPO':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 20),
      };
    case 'WR':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 14),
      };
    case 'PROC':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 14),
      };
    case 'PCV':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 14),
      };

    case 'CO':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        method: selectVal(0, 'ema'),
        period: lineValue(0, 10),
        period2: lineValue(1, 3),
      };

    case 'FKD':
      return {
        K: lineValue(0, 14),
        D: lineValue(1, 3),
        color: lineColor(0),
        thickness: 2,
        style: 's',
        showTracker: true,
        color2: lineColor(1),
        thickness2: 2,
        style2: 's',
        showTracker2: true,
        buy: lineValue(2, 20),
        sale: lineValue(3, 80),
      };

    case 'SKD':
      return {
        K: lineValue(1, 14),
        D: lineValue(2, 3),
        color: lineColor(1),
        thickness: 2,
        style: 's',
        showTracker: true,
        color2: lineColor(2),
        thickness2: 2,
        style2: 's',
        showTracker2: true,
        buy: lineValue(3, 20),
        sale: lineValue(4, 80),
        method: selectVal(0, 'sma'),
      };

    case 'MI':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        method: selectVal(0, 'ema'),
        period: lineValue(0, 25),
        period2: lineValue(1, 9),
      };

    case 'UO':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 7),
        period2: lineValue(1, 14),
        period3: lineValue(2, 28),
        buy: lineValue(3, 30),
        sell: lineValue(4, 70),
      };

    case 'Vector':
      return {
        PERIOD: {
          style: 's',
          thickness: 2,
          color: lineColor(0),
          showTracker: true,
          enabled: lineEnabled(0),
          period: lineValue(0, 14),
        },
        MA: {
          style: 's',
          thickness: 2,
          color: lineColor(1),
          showTracker: true,
          enabled: lineEnabled(1),
          period: lineValue(1, 9),
        },
      };

    case 'WAD':
    case 'ACD':
    case 'OBV':
      return { style: 's', thickness: 2, color: mainColor, showTracker: true };

    case 'Trix':
      return {
        TRIX: {
          style: 's',
          thickness: 2,
          color: lineColor(0),
          showTracker: true,
          period: lineValue(0, 15),
        },
        SIGNAL: {
          style: 's',
          thickness: 2,
          color: lineColor(1),
          showTracker: true,
          period: lineValue(1, 9),
        },
      };

    // VOLUME
    case 'Volume':
      return {
        upColor: lineColor(0),
        downColor: lineColor(1),
      };

    case 'VE':
      return {
        period: lineValue(0, 20),
        volumeColor: lineColor(0),
        factor: lineValue(1, 10),
        ENVELOPE: {
          style: 's',
          thickness: 2,
          color: lineColor(0),
          showTracker: true,
          upperColor: lineColor(1),
          lowerColor: lineColor(2),
        },
      };

    case 'VAP':
      return { upColor: lineColor(0), downColor: lineColor(1) };

    case 'VOSC':
      return {
        short: lineValue(0, 5),
        long: lineValue(1, 10),
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
      };

    case 'VROC':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 10),
      };

    case 'EOM':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        method: selectVal(0, 'sma'),
        period: lineValue(0, 14),
      };

    case 'FI':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        method: selectVal(0, 'ema'),
        period: lineValue(0, 13),
      };

    case 'MFI':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 14),
        buy: lineValue(1, 20),
        sell: lineValue(2, 80),
      };

    case 'NVI':
      // lines: [0]=Period1, [1]=Period2  (normal NVI line uses ta.color)
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        PERIOD: [
          {
            enabled: lineEnabled(0),
            period: lineValue(0, 255),
            style: 's',
            thickness: 2,
            color: lineColor(0),
            showTracker: true,
          },
          {
            enabled: lineEnabled(1),
            period: lineValue(1, 255),
            style: 's',
            thickness: 2,
            color: lineColor(1),
            showTracker: true,
          },
        ],
      };

    case 'VR':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 14),
      };

    // MOMENTUM
    case 'ENV':
      // lines: [0]=Period, [1]=E1σ+, [2]=E1σ-, [3]=E2σ+, [4]=E2σ-, [5]=E3σ+, [6]=E3σ-, [7]=E4σ+, [8]=E4σ-, [9]=E5σ+, [10]=E5σ-
      return {
        enabled: true,
        period: lineValue(0, 20),
        style: 'd',
        thickness: 2,
        color: lineColor(0),
        showTracker: true,
        ENVELOPES: [
          {
            style: 's',
            thickness: 2,
            color: lineColor(1),
            showTracker: true,
            enabled: lineEnabled(1),
            factor: lineValue(1, 2.5),
            posColor: lineColor(1),
            negColor: lineColor(2),
          },
          {
            style: 's',
            thickness: 2,
            color: lineColor(3),
            showTracker: true,
            enabled: lineEnabled(3),
            factor: lineValue(3, 5),
            posColor: lineColor(3),
            negColor: lineColor(4),
          },
          {
            style: 's',
            thickness: 2,
            color: lineColor(5),
            showTracker: true,
            enabled: lineEnabled(5),
            factor: lineValue(5, 10),
            posColor: lineColor(5),
            negColor: lineColor(6),
          },
          {
            style: 's',
            thickness: 2,
            color: lineColor(7),
            showTracker: true,
            enabled: lineEnabled(7),
            factor: lineValue(7, 25),
            posColor: lineColor(7),
            negColor: lineColor(8),
          },
          {
            style: 's',
            thickness: 2,
            color: lineColor(9),
            showTracker: true,
            enabled: lineEnabled(9),
            factor: lineValue(9, 50),
            posColor: lineColor(9),
            negColor: lineColor(10),
          },
        ],
      };

    case 'MOM':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 10),
      };

    case 'RMI':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        method: selectVal(0, 'mema'),
        period: lineValue(0, 14),
        momentum: lineValue(1, 3),
        buy: lineValue(2, 30),
        sell: lineValue(3, 70),
      };

    case 'RSI':
      // lines: [0]=P1, [1]=P2, [2]=Oversold, [3]=Overbought
      return {
        buy: lineValue(2, 30),
        sale: lineValue(3, 70),
        atr: [
          {
            period: lineValue(0, 14),
            ta: selectVal(0, 'ema'),
            color: lineColor(0),
            thickness: 2,
            style: 's',
            enabled: lineEnabled(0),
            showTracker: true,
          },
          {
            period: lineValue(1, 9),
            ta: selectVal(1, 'ema'),
            color: lineColor(1),
            thickness: 2,
            style: 's',
            enabled: lineEnabled(1),
            showTracker: true,
          },
        ],
      };

    // PRICE
    case 'POSC':
      return {
        short: lineValue(0, 12),
        long: lineValue(1, 26),
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
      };

    case 'PRR':
      return {
        width: 1,
        upColor: mainColor,
        downColor: mainColor,
      };

    case 'VWAP':
      return { style: 's', thickness: 2, color: mainColor, showTracker: true };

    case 'WC':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        weight: lineValue(0, 2),
      };

    case 'FIB':
      // lines: [0]=0%, [1]=23.6%, [2]=38.2%, [3]=50%, [4]=61.8%
      return {
        p0: { color: lineColor(0), thickness: 2, style: 's', showTracker: true },
        p1: { color: lineColor(1), thickness: 2, style: 's', showTracker: true },
        p2: { color: lineColor(2), thickness: 2, style: 's', showTracker: true },
        p3: { color: lineColor(3), thickness: 2, style: 's', showTracker: true },
        p4: { color: lineColor(4), thickness: 2, style: 's', showTracker: true },
      };

    // OTHER
    case 'TP':
      return { color: mainColor, period: lineValue(0, 10) };

    case 'Turnover':
      return { color: mainColor, height: 1 };

    case 'CC':
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: lineValue(0, 10),
        short: lineValue(1, 11),
        long: lineValue(2, 14),
      };

    case 'KC':
      // lines: [0]=KC period, [1]=ATR1σ+, [2]=ATR1σ-, [3]=ATR2σ+, [4]=ATR2σ-, [5]=ATR3σ+, [6]=ATR3σ-
      return {
        style: 's',
        thickness: 2,
        color: lineColor(0),
        showTracker: true,
        enabled: true,
        period: lineValue(0, 14),
        ATR: [
          {
            style: 's',
            thickness: 2,
            color: lineColor(1),
            showTracker: true,
            enabled: lineEnabled(1),
            period: lineValue(1, 10),
            posColor: lineColor(1),
            negColor: lineColor(2),
          },
          {
            style: 's',
            thickness: 2,
            color: lineColor(3),
            showTracker: true,
            enabled: lineEnabled(3),
            period: lineValue(3, 20),
            posColor: lineColor(3),
            negColor: lineColor(4),
          },
          {
            style: 's',
            thickness: 2,
            color: lineColor(5),
            showTracker: true,
            enabled: lineEnabled(5),
            period: lineValue(5, 30),
            posColor: lineColor(5),
            negColor: lineColor(6),
          },
        ],
      };

    case 'RC': {
      const rcMkt = RC_MKT_MAP[(ta.selectValues?.[1] ?? 'S&P500').toLowerCase()] ?? '.SPX';
      return {
        color: mainColor,
        enabled: true,
        period: lineValue(0, 14),
        showTracker: true,
        style: 's',
        thickness: 2,
        mkt: rcMkt,
        data: ta.externalData ?? '',
      };
    }

    case 'UI':
      return {
        style: 's',
        thickness: 2,
        color: lineColor(0),
        showTracker: true,
        period: lineValue(0, 14),
      };

    case 'RHI':
      // lines: [0]=line A (period+color), [1]=line B (color only)
      return {
        period: lineValue(0, 26),
        colorA: lineColor(0),
        colorB: lineColor(1),
        thickness: 2,
        style: 's',
        showTracker: true,
      };

    default:
      return {
        style: 's',
        thickness: 2,
        color: mainColor,
        showTracker: true,
        period: mainPeriod,
        enabled: true,
      };
  }
}

export function buildTAPayload(activeTA: TAItem[], symbol = ''): {
  overlay: string;
  indicator: string;
} {
  const overlayObj: Record<string, unknown> = {};
  const indicatorObj: Record<string, unknown> = {};
  for (const ta of activeTA) {
    const bundleKey = BUNDLE_KEY_MAP[ta.key] ?? ta.key;
    const isOverlay = OVERLAY_IDS.has(ta.key);
    const serialized = serializeTA(ta);
    if (isOverlay) {
      overlayObj[bundleKey] = serialized;
    } else {
      const h = ta.paneHeight ?? 1;
      indicatorObj[bundleKey] =
        serialized !== null &&
        typeof serialized === 'object' &&
        !Array.isArray(serialized)
          ? { ...(serialized as Record<string, unknown>), height: h }
          : serialized;
    }
  }
  return {
    overlay: encodeURIComponent(JSON.stringify(overlayObj)),
    indicator: encodeURIComponent(JSON.stringify(indicatorObj)),
  };
}

export interface BuildMessageProps {
  symbol: string;
  interval: IntervalKey;
  period: PeriodKey;
  chartType: ChartTypeKey;
  candlestickColors: CandlestickColors;
  lineConfig: LineConfig;
  barColor: string;
  activeTA: TAItem[];
  comparisonList: ComparisonItem[];
  comparisonMode: boolean;
  view: ViewState;
  drawingConfigs: Record<DrawingToolKey, DrawingToolConfig>;
}

export function buildMessage(
  props: BuildMessageProps,
  chartData: string,
  compareChartData: Record<string, string>,
): ChartBundlePayload {
  const {
    symbol,
    interval,
    period,
    chartType,
    candlestickColors,
    lineConfig,
    barColor,
    view,
    activeTA,
    comparisonList,
    comparisonMode,
    drawingConfigs,
  } = props;
  const { overlay, indicator } = buildTAPayload(activeTA, symbol);

  const edges = csvEdgeDates(chartData);
  const showFrom = getShowFromByPeriod(period, edges.showTo);

  return buildChartPayload(symbol, interval, period, chartType, {
    chartData: '\n' + chartData,
    showFrom,
    showTo: edges.showTo,
    showMainChart: view.showMainChart,
    showTA: view.showTA,
    showScrollView: view.showTimelineScroller,
    showCrosshair: view.showCrosshair,
    showDrawTool: view.showTrendlines,
    showCompare: comparisonMode && comparisonList.length > 0,
    showTracker: view.showTracker,
    showToolTips: view.showCrosshairTooltip,
    overlay,
    indicator,
    chartOption: buildChartOption(
      chartType,
      candlestickColors,
      lineConfig,
      barColor,
      comparisonMode,
    ),
    drawToolOption: buildDrawToolOption(drawingConfigs),
    compareData: comparisonList.map((c) => ({
      name: c.symbol,
      // Main stock reuses the already-fetched chartData; others come from compareChartData
      chartData:
        c.symbol === symbol
          ? '\n' + chartData
          : '\n' + (compareChartData[c.symbol] ?? ''),
      option: {
        color: hex(c.color),
        thickness: c.thickness,
        style: LINE_STYLE_MAP[c.style] ?? 's',
      },
    })),
  });
}
